import { RefundExecutionMethod, ReturnDesiredAction, ReturnItemStatus, ReturnMethod, ReturnReason } from '@sleed/bizcommerce-api';
import type { PillVariant } from '~/components/pill';

/**
 * Label-key and pill maps for the returns module's enums.
 *
 * The enums themselves are VALUE-IMPORTED from the generated client rather than mirrored as local
 * literals (as `order-status.ts` does): they were generated from `ReturnEnums.cs` in this same
 * change, so a server-side renumbering shows up as a compile error here instead of as silently
 * mislabelled rows. Only the label keys and pill tones — which are presentation, not contract —
 * live in this file.
 *
 * Every `labelKey` is a suffix under `orders.returns.*` in the locale files.
 */

export interface StatusPill {
	variant: PillVariant;
	/** i18n key suffix under `orders.returns.status.*`. */
	labelKey: string;
}

const STATUS_PILLS: Record<number, StatusPill> = {
	[ReturnItemStatus.New]: { variant: 'primary', labelKey: 'new' },
	[ReturnItemStatus.UnderReview]: { variant: 'warning', labelKey: 'underReview' },
	[ReturnItemStatus.Approved]: { variant: 'info', labelKey: 'approved' },
	[ReturnItemStatus.AwaitingProduct]: { variant: 'neutral', labelKey: 'awaitingProduct' },
	[ReturnItemStatus.InProgress]: { variant: 'info', labelKey: 'inProgress' },
	[ReturnItemStatus.Completed]: { variant: 'success', labelKey: 'completed' },
	[ReturnItemStatus.Rejected]: { variant: 'danger', labelKey: 'rejected' }
};

export function statusPill(status: number | null | undefined): StatusPill {
	if (status == null) return { variant: 'neutral', labelKey: 'unknown' };
	return STATUS_PILLS[status] ?? { variant: 'neutral', labelKey: 'unknown' };
}

/** Every status, in workflow order — the status filter's option list. */
export const ALL_STATUSES: readonly ReturnItemStatus[] = [
	ReturnItemStatus.New,
	ReturnItemStatus.UnderReview,
	ReturnItemStatus.Approved,
	ReturnItemStatus.AwaitingProduct,
	ReturnItemStatus.InProgress,
	ReturnItemStatus.Completed,
	ReturnItemStatus.Rejected
];

/** i18n suffix under `orders.returns.status.*`. */
export const statusLabelKey = (status: number | null | undefined) => `orders.returns.status.${statusPill(status).labelKey}`;

const REASON_LABEL_KEYS: Record<number, string> = {
	[ReturnReason.None]: 'none',
	[ReturnReason.ChangedMind]: 'changedMind',
	[ReturnReason.PoorCondition]: 'poorCondition',
	[ReturnReason.Defective]: 'defective',
	[ReturnReason.WrongProduct]: 'wrongProduct',
	[ReturnReason.MissingPart]: 'missingPart',
	[ReturnReason.FewerPieces]: 'fewerPieces',
	[ReturnReason.MorePieces]: 'morePieces',
	[ReturnReason.WrongColour]: 'wrongColour',
	[ReturnReason.WrongSize]: 'wrongSize'
};

export const reasonLabelKey = (reason: number | null | undefined) =>
	`orders.returns.reason.${REASON_LABEL_KEYS[reason ?? ReturnReason.None] ?? 'none'}`;

const ACTION_LABEL_KEYS: Record<number, string> = {
	[ReturnDesiredAction.None]: 'none',
	[ReturnDesiredAction.Refund]: 'refund',
	[ReturnDesiredAction.Replacement]: 'replacement',
	[ReturnDesiredAction.Credit]: 'credit'
};

export const actionLabelKey = (action: number | null | undefined) =>
	`orders.returns.action.${ACTION_LABEL_KEYS[action ?? ReturnDesiredAction.None] ?? 'none'}`;

/**
 * The wishes a customer can actually express. `None` is never one of them, so it is offered neither
 * as a filter value nor as something the administrator can set.
 */
export const SELECTABLE_ACTIONS: readonly ReturnDesiredAction[] = [
	ReturnDesiredAction.Refund,
	ReturnDesiredAction.Replacement,
	ReturnDesiredAction.Credit
];

/** The actions that end in money going back — the ones a refund-execution record settles. */
export const REFUNDING_ACTIONS: readonly ReturnDesiredAction[] = [ReturnDesiredAction.Refund, ReturnDesiredAction.Credit];

export const isRefundingAction = (action: number | null | undefined) => REFUNDING_ACTIONS.includes(action as ReturnDesiredAction);

const REFUND_METHOD_LABEL_KEYS: Record<number, string> = {
	[RefundExecutionMethod.NotExecuted]: 'notExecuted',
	[RefundExecutionMethod.BankTransfer]: 'bankTransfer',
	[RefundExecutionMethod.PaymentProviderReversal]: 'providerReversal',
	[RefundExecutionMethod.OriginalPaymentInstrument]: 'originalInstrument',
	[RefundExecutionMethod.CashInStore]: 'cashInStore',
	// The enum member is the generated client's; the label is not. A credit is paid out with a coupon the
	// administrator creates by hand, so that is what the back office calls instrument 5.
	[RefundExecutionMethod.GiftCard]: 'coupon'
};

export const ALL_REFUND_METHODS: readonly RefundExecutionMethod[] = [
	RefundExecutionMethod.NotExecuted,
	RefundExecutionMethod.BankTransfer,
	RefundExecutionMethod.PaymentProviderReversal,
	RefundExecutionMethod.OriginalPaymentInstrument,
	RefundExecutionMethod.CashInStore,
	RefundExecutionMethod.GiftCard
];

export const refundMethodLabelKey = (method: number | null | undefined) =>
	`orders.returns.refundMethod.${REFUND_METHOD_LABEL_KEYS[method ?? RefundExecutionMethod.NotExecuted] ?? 'notExecuted'}`;

/** `ReturnMethod` is a [Flags] bitmask, so a value can carry several of these at once. */
export const RETURN_METHOD_FLAGS: readonly { flag: ReturnMethod; labelKey: string }[] = [
	{ flag: ReturnMethod.CourierPickup, labelKey: 'orders.returns.method.courierPickup' },
	{ flag: ReturnMethod.LockerDropOff, labelKey: 'orders.returns.method.lockerDropOff' },
	{ flag: ReturnMethod.FreightCarrier, labelKey: 'orders.returns.method.freightCarrier' }
];

/** The label keys of every flag set in a `ReturnMethod` mask (empty when nothing is set). */
export const returnMethodLabelKeys = (mask: number | null | undefined): string[] =>
	mask ? RETURN_METHOD_FLAGS.filter(m => (mask & m.flag) !== 0).map(m => m.labelKey) : [];

/**
 * The statuses the list hides when no status filter is chosen: the default view is the open queue,
 * and a closed request is only interesting when someone asks for it.
 */
export const DEFAULT_HIDDEN_STATUSES: readonly ReturnItemStatus[] = [ReturnItemStatus.Rejected, ReturnItemStatus.Completed];

/** A closed piece owes the customer nothing further — no record on it is pending work. */
export const isClosedStatus = (status: number | null | undefined) =>
	status === ReturnItemStatus.Completed || status === ReturnItemStatus.Rejected;

/**
 * Whether a settlement or dispatch can be recorded on a piece in this status — approved and still open. Mirrors the
 * server's `ReturnStatusTransitions.AcceptsRecords`, which refuses the record otherwise (`RecordNotAllowedInStatus`).
 */
export const acceptsRecords = (status: number | null | undefined) =>
	status === ReturnItemStatus.Approved || status === ReturnItemStatus.AwaitingProduct || status === ReturnItemStatus.InProgress;
